import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { EMPTY_MENU_ORDER, MenuOrderConfig, isMenuOrderConfig } from '../utils/menu-order.utils';
import { LoggerService } from './logger.service';

/** API del propio servidor de la app (src/server/config-api.ts), guardada en SQLite. */
const MENU_ORDER_API = '/app-config/menu-order';
/** Orden inicial publicado con la app: rige mientras nadie haya guardado uno. */
const MENU_ORDER_SEED = '/assets/config/menu-order.json';

interface StoredMenuOrder {
    value: unknown;
}

/**
 * Orden del menú, el mismo para todos los usuarios. Se lee una vez por sesión:
 * 1. el guardado en el servidor (SQLite);
 * 2. si no hay, el archivo inicial de assets;
 * 3. si tampoco, el orden del backend. Nunca bloquea la carga del menú.
 */
@Injectable({ providedIn: 'root' })
export class MenuOrderService {
    private readonly http = inject(HttpClient);
    private readonly logger = inject(LoggerService);

    private order$: Observable<MenuOrderConfig> | null = null;

    load(): Observable<MenuOrderConfig> {
        this.order$ ??= this.fetch().pipe(shareReplay(1));
        return this.order$;
    }

    /** Guarda el orden para todos. Solo quien administra páginas (lo valida el servidor). */
    save(order: MenuOrderConfig): Observable<MenuOrderConfig> {
        return this.http.put<StoredMenuOrder>(MENU_ORDER_API, { value: order }).pipe(
            map(() => order),
            tap(() => (this.order$ = of(order))),
        );
    }

    private fetch(): Observable<MenuOrderConfig> {
        return this.http.get<StoredMenuOrder>(MENU_ORDER_API).pipe(
            map((stored) => {
                if (!isMenuOrderConfig(stored.value)) {
                    throw new Error('Orden guardado con forma inválida');
                }
                return stored.value;
            }),
            catchError(() => this.fetchSeed()),
        );
    }

    private fetchSeed(): Observable<MenuOrderConfig> {
        return this.http.get<unknown>(MENU_ORDER_SEED).pipe(
            map((seed) => (isMenuOrderConfig(seed) ? seed : EMPTY_MENU_ORDER)),
            catchError((error) => {
                this.logger.error('MenuOrderService: sin orden guardado ni archivo inicial; se usa el orden del backend', error);
                return of(EMPTY_MENU_ORDER);
            }),
        );
    }
}
